import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";
import drEasyApi from "../Api/Customapi";

const excuseAPI = {
  getAll: async () => {
    const { data } = await drEasyApi.get("/api/Excuses/GetAll");
    return data;
  },

  create: async (excuseData) => {
    const { data } = await drEasyApi.post("/api/Excuses/Create", excuseData);
    return data;
  },

  edit: async (excuseData) => {
    const { data } = await drEasyApi.put("/api/Excuses/Edit", excuseData);
    return data;
  },

  delete: async (id) => {
    const { data } = await drEasyApi.delete("/api/Excuses/Delete", {
      params: { Id: id },
    });
    return data;
  },

  // Assignment APIs
  assignExcuse: async (assignmentData) => {
    const { data } = await drEasyApi.post("/api/Excuses/Assign", assignmentData);
    return data;
  },

  removeAssignment: async ({ employeeId, excuseId }) => {
    const { data } = await drEasyApi.delete("/api/Excuses/RemoveAssignment", {
      params: { EmployeeId: employeeId, ExcuseId: excuseId },
    });
    return data;
  },

  getAssignments: async (employeeId) => {
    const { data } = await drEasyApi.get("/api/Excuses/GetAssignments", {
      params: { EmployeeId: employeeId },
    });
    return data;
  },

  createExcuseRequest: async (requestData) => {
    const { data } = await drEasyApi.post("/api/Excuses/CreateExcuseRequest", requestData);
    return data;
  },

  getExcuseRequests: async (params) => {
    const { data } = await drEasyApi.get("/api/Excuses/GetExcuseRequests", {
      params: params,
    });
    return data;
  },

  respondExcuseRequest: async (responseData) => {
    const { data } = await drEasyApi.post("/api/Excuses/RespondExcuseRequest", responseData);
    return data;
  },
};

// Custom hooks
export const useExcuses = () => {
  const queryClient = useQueryClient();

  // Get all excuses
  const useGetExcuses = () => {
    return useQuery({
      queryKey: ["excuses"],
      queryFn: excuseAPI.getAll,
      onError: (error) => {
        message.error("Failed to fetch excuses");
        console.error("Error fetching excuses:", error);
      },
    });
  };

  // Create excuse
  const useCreateExcuse = () => {
    return useMutation({
      mutationFn: excuseAPI.create,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["excuses"] });
        message.success("Excuse created successfully");
      },
      onError: (error) => {
        message.error("Failed to create excuse");
        console.error("Error creating excuse:", error);
      },
    });
  };

  // Edit excuse
  const useEditExcuse = () => {
    return useMutation({
      mutationFn: excuseAPI.edit,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["excuses"] });
        message.success("Excuse updated successfully");
      },
      onError: (error) => {
        message.error("Failed to update excuse");
        console.error("Error updating excuse:", error);
      },
    });
  };

  // Delete excuse
  const useDeleteExcuse = () => {
    return useMutation({
      mutationFn: excuseAPI.delete,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["excuses"] });
        message.success("Excuse deleted successfully");
      },
      onError: (error) => {
        message.error("Failed to delete excuse");
        console.error("Error deleting excuse:", error);
      },
    });
  };

  // Get employee excuse assignments
  const useGetAssignments = (employeeId) => {
    return useQuery({
      queryKey: ["excuse-assignments", employeeId],
      queryFn: () => excuseAPI.getAssignments(employeeId),
      enabled: !!employeeId,
      onError: (error) => {
        message.error("Failed to fetch excuse assignments");
        console.error("Error fetching assignments:", error);
      },
    });
  };

  // Assign excuse to employee
  const useAssignExcuse = () => {
    return useMutation({
      mutationFn: excuseAPI.assignExcuse,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["excuse-assignments"] });
        message.success("Excuse assigned successfully");
      },
      onError: (error) => {
        message.error("Failed to assign excuse");
        console.error("Error assigning excuse:", error);
      },
    });
  };

  // Remove excuse assignment
  const useRemoveAssignment = () => {
    return useMutation({
      mutationFn: excuseAPI.removeAssignment,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["excuse-assignments"] });
        message.success("Excuse assignment removed successfully");
      },
      onError: (error) => {
        message.error("Failed to remove excuse assignment");
        console.error("Error removing assignment:", error);
      },
    });
  };

  // Create excuse request
  const useCreateExcuseRequest = () => {
    return useMutation({
      mutationFn: excuseAPI.createExcuseRequest,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["excuse-requests"] });
        message.success("Excuse request created successfully");
      },
      onError: (error) => {
        message.error(error.response.data[0]);
        console.error("Error creating excuse request:", error);
      },
    });
  };

  const useGetExcuseRequests = (params) => {
    return useQuery({
      queryKey: ["excuse-requests", params],
      queryFn: () => excuseAPI.getExcuseRequests(params),
      onError: (error) => {
        message.error("Failed to fetch excuse requests");
        console.error("Error fetching excuse requests:", error);
      },
    });
  };

  // Respond to excuse request
  const useRespondExcuseRequest = () => {
    return useMutation({
      mutationFn: excuseAPI.respondExcuseRequest,
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ["excuse-requests"] });
        message.success("Response submitted successfully");
      },
      onError: (error) => {
        message.error("Failed to respond to request");
        console.error("Error responding to request:", error);
      },
    });
  };

  return {
    useGetExcuses,
    useCreateExcuse,
    useEditExcuse,
    useDeleteExcuse,
    useGetAssignments,
    useAssignExcuse,
    useRemoveAssignment,
    useCreateExcuseRequest,
    useGetExcuseRequests,
    useRespondExcuseRequest,
  };
};